import { connect } from 'react-redux';
import { get, orderBy, round, sumBy } from 'lodash';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';

import { getUseSupplierProductSecondaryPrices } from '@selectors/featureProps';
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import {
  Button,
  Dropdown,
  Franco,
  HoverTooltip,
  ToggleSwitch,
} from '@commons/utils/styledLibraryComponents';
import { ConfirmationModal } from '@commons/Modals/ConfirmationModal';
import {
  convertPriceToPackagingById,
  convertQuantityToPackagingById,
} from '@commons/utils/packagings';
import {
  GENERIC_MODAL_CANCEL_BUTTON,
  GENERIC_MODAL_CONFIRM_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getTheme } from '@commons/utils/theme';
import DisplayNumber from '@commons/DisplayNumber';
import Footer from '@commons/Footer/Footer';
import NavigationBar from '@commons/NavigationBar';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import invoiceService from '@services/invoice';

import { INVOICE_CONTROL_MAPPING_SOURCE, INVOICE_CONTROL_STATUS } from '../commons/constants';
import { NavBarContainer } from '../invoiceControlDetails/styledComponents';

import {
  ActionsContainer,
  AddedLineIndicator,
  Container,
  EmptyDiv,
  EmptyDivContainer,
  FooterButtonsContainer,
  HeaderContainer,
  IconButton,
  IconButtonContainer,
  InvoiceLineNameContainer,
  InvoiceLineTextContainer,
  MappingContainer,
  PackagingContainer,
  PackagingListContainer,
  ToggleContainer,
} from './styledComponents';
import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DisabledTooltip } from '@commons/DisabledTooltip';

const UNMAPPED_TO_ORDER_STATUSES = new Set([
  INVOICE_CONTROL_STATUS.UNKNOWN_REFERENCE,
  INVOICE_CONTROL_STATUS.NO_CORRESPONDING_ORDER,
  INVOICE_CONTROL_STATUS.ADDITIONAL_REFERENCE,
]);

const isInvoiceControlMappedToOrder = ({ orderId, status }) =>
  !!orderId && !UNMAPPED_TO_ORDER_STATUSES.has(status);

const isInvoiceControlNotMappedToOrder = (row) =>
  row.invoiceControlId && !isInvoiceControlMappedToOrder(row);

const isManualInvoiceControlMapping = (row) =>
  row.isManuallyMapped || row.invoiceControlMappingSource === INVOICE_CONTROL_MAPPING_SOURCE.MANUAL;

const getOrderLineDisplayPriority = (row) => {
  if (row.status === INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL) {
    return 2;
  }

  if (row.isManuallyMapped) {
    return 1;
  }

  return 0;
};

const getFormattedLineByLineDataKey = (row, index = null) => {
  if (row.rowKey) {
    return row.rowKey;
  }

  if (row.invoiceControlId) {
    return `invoice-control-${row.invoiceControlId}`;
  }

  if (row.orderId || row.spId) {
    return `order-${row.orderId ?? 'none'}-supplier-product-${row.spId ?? 'none'}`;
  }

  return `unknown-${index}`;
};

export const InvoiceControlLineByLine = (props) => {
  const {
    match: { path, params },
    currency,
    pageLoading,
    pageLoaded,
    openGenericModal,
    showErrorMessage,
    showSuccessMessage,
    useSupplierProductSecondaryPrices,
  } = props;

  const [invoiceControlList, setInvoiceControlList] = useState(null);
  const [hasAssociatedOrders, setHasAssociatedOrders] = useState(false);
  const [associatedOrders, setAssociatedOrders] = useState([]);
  const [draftMappingsByInvoiceControlId, setDraftMappingsByInvoiceControlId] = useState({});
  const [manuallyMappedInvoiceControlIds, setManuallyMappedInvoiceControlIds] = useState([]);
  const [displayProductOrderNotMapped, setDisplayProductOrderNotMapped] = useState(false);
  const [invoiceTotalGap, setInvoiceTotalGap] = useState(0);
  const [conformLines, setConformLines] = useState(0);
  const [formattedLineByLineDataByOrderIdAndSpId, setFormattedLineByLineDataByOrderIdAndSpId] =
    useState({});
  const [filteredFormattedLineByLineData, setFilteredFormattedLineByLineData] = useState([]);
  const [isFooterDisplay, setIsFooterDisplay] = useState(false);

  const theme = getTheme();

  const packagingListContainerRef = useRef(null);

  useEffect(() => {
    (async () => {
      await getICLAndLineByLineData();
    })();
  }, []);

  useEffect(() => {
    formatLineByLineData(Object.values(formattedLineByLineDataByOrderIdAndSpId));
  }, [displayProductOrderNotMapped]);

  useEffect(() => {
    const filteredData = filterFormattedLineByLineData();

    const totalGap = sumBy(filteredData, 'updatedTotalGap');

    setInvoiceTotalGap(totalGap);
    setConformLines(
      filteredData.filter(
        ({ status, updatedTotalGap }) =>
          status === INVOICE_CONTROL_STATUS.NO_GAP || updatedTotalGap === 0,
      ).length,
    );
    setIsFooterDisplay(
      filteredData.some(
        ({ updatePriceChecked, updatePackagingChecked }) =>
          updatePriceChecked || updatePackagingChecked,
      ),
    );
    setFilteredFormattedLineByLineData(filteredData);
  }, [formattedLineByLineDataByOrderIdAndSpId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        packagingListContainerRef.current &&
        !packagingListContainerRef.current.contains(event.target)
      ) {
        const newFormattedData = {};
        for (const [key, row] of Object.entries(formattedLineByLineDataByOrderIdAndSpId)) {
          newFormattedData[key] = { ...row, displayPackagingDropdown: false };
        }
        setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [packagingListContainerRef, formattedLineByLineDataByOrderIdAndSpId]);

  const filterFormattedLineByLineData = () => {
    const invoiceControlLinesLinkedToOrders = Object.values(
      formattedLineByLineDataByOrderIdAndSpId,
    ).filter((row) => {
      if (!isInvoiceControlMappedToOrder(row)) {
        return false;
      }

      if (displayProductOrderNotMapped) {
        return true;
      }

      return row.status !== INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL;
    });

    const invoiceControlLinesNotLinkedToOrders = Object.values(
      formattedLineByLineDataByOrderIdAndSpId,
    ).filter(isInvoiceControlNotMappedToOrder);

    const allFormattedLines = [
      ...orderBy(
        invoiceControlLinesLinkedToOrders,
        ['orderDeliveryDate', 'orderId', getOrderLineDisplayPriority, 'invoiceNumber', 'totalGap'],
        ['asc', 'asc', 'asc', 'asc', 'asc'],
      ),
      ...orderBy(invoiceControlLinesNotLinkedToOrders, ['totalGap'], ['asc']),
    ];

    return allFormattedLines;
  };

  const getDraftMapping = (row) => draftMappingsByInvoiceControlId[row.invoiceControlId] || {};

  const getSelectedOrderId = (row) => {
    const draftMapping = getDraftMapping(row);

    if (draftMapping.orderId) {
      return draftMapping.orderId;
    }

    return associatedOrders[0]?.orderId || null;
  };

  const getDraftMappedSupplierProductIds = (invoiceControlId) =>
    Object.entries(draftMappingsByInvoiceControlId).reduce(
      (acc, [mappedInvoiceControlId, draft]) => {
        if (mappedInvoiceControlId === invoiceControlId) {
          return acc;
        }

        if (!draft.supplierProductId) {
          return acc;
        }

        acc.add(draft.supplierProductId);
        return acc;
      },
      new Set(),
    );

  const getAvailableReferenceOptions = (orderId, invoiceControlId) => {
    const draftMappedSupplierProductIds = getDraftMappedSupplierProductIds(invoiceControlId);

    return Object.values(formattedLineByLineDataByOrderIdAndSpId).filter((row) => {
      if (row.status !== INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL) {
        return false;
      }

      if (row.orderId !== orderId) {
        return false;
      }

      return !draftMappedSupplierProductIds.has(row.spId);
    });
  };

  const formatOrderDropdownItems = () =>
    associatedOrders.map(({ orderId, reference }) => ({
      id: orderId,
      value: reference,
    }));

  const formatReferenceDropdownItems = (referenceOptions) =>
    referenceOptions.map(({ spId, spName, spSku }) => ({
      id: spId,
      value: `${spName || '-'}${spSku ? ` (${spSku})` : ''}`,
    }));

  const getSelectedSupplierProductId = (row) => {
    const draftMapping = getDraftMapping(row);

    if (draftMapping.supplierProductId) {
      return draftMapping.supplierProductId;
    }

    return null;
  };

  const handleMappingOrderChange = (row, orderId) => {
    setDraftMappingsByInvoiceControlId({
      ...draftMappingsByInvoiceControlId,
      [row.invoiceControlId]: {
        orderId,
        supplierProductId: null,
      },
    });
  };

  const handleMappingReferenceChange = (row, supplierProductId) => {
    const orderId = getSelectedOrderId(row);

    setDraftMappingsByInvoiceControlId({
      ...draftMappingsByInvoiceControlId,
      [row.invoiceControlId]: {
        orderId,
        supplierProductId,
      },
    });
  };

  const formatLineByLineData = (fetchedData, manualMappedIds = manuallyMappedInvoiceControlIds) => {
    const formattedData = fetchedData.map((ICDetail) => {
      const masterPackaging =
        ICDetail.spPackagings &&
        ICDetail.spPackagings.find(
          ({ masterSupplierProductPackagingId }) => !masterSupplierProductPackagingId,
        );

      const spInvoicePackaging =
        (ICDetail.spPackagings &&
          ICDetail.spPackagings.find(({ isUsedInInvoice }) => isUsedInInvoice)) ||
        null;

      const spMasterUnit = masterPackaging ? masterPackaging.unit : null;

      const spPrice = getSpPrice(ICDetail);

      return {
        ...ICDetail,
        spMasterUnit,
        spInvoicePackaging,
        updatePriceIcon: ICDetail.updatePriceIcon || '/images/inpulse/new-price-black-small.svg',
        updatePriceChecked: ICDetail.updatePriceChecked || false,
        updatedSpReceivedQuantity:
          ICDetail.updatedSpReceivedQuantity ||
          (ICDetail.spReceivedQuantity != null ? ICDetail.spReceivedQuantity : null),
        updatedSpPrice: ICDetail.updatedSpPrice || (spPrice != null ? spPrice : null),
        updatedReceivedTotal:
          ICDetail.updatedReceivedTotal ||
          (ICDetail.receivedTotal != null ? ICDetail.receivedTotal : null),
        updatedTotalGap:
          ICDetail.updatedTotalGap != null
            ? ICDetail.updatedTotalGap
            : ICDetail.totalGap != null
            ? ICDetail.totalGap
            : null,
        updatedInvoicePackaging: ICDetail.updatedInvoicePackaging || spInvoicePackaging,
        updatedSpPackagingName: ICDetail.updatedSpPackagingName || ICDetail.spPackagingName,
        updatedSpPackagings: ICDetail.updatedSpPackagings || ICDetail.spPackagings,
        updatePackagingIcon:
          ICDetail.updatePackagingIcon || '/images/inpulse/stock-black-small.svg',
        updatePackagingChecked: ICDetail.updatePackagingChecked || false,
        displayPackagingDropdown: ICDetail.displayPackagingDropdown || false,
        isManuallyMapped:
          isManualInvoiceControlMapping(ICDetail) ||
          manualMappedIds.includes(ICDetail.invoiceControlId),
      };
    });

    setFormattedLineByLineDataByOrderIdAndSpId(
      formattedData.reduce((acc, row, index) => {
        const rowKey = getFormattedLineByLineDataKey(row, index);

        acc[rowKey] = {
          ...row,
          rowKey,
        };
        return acc;
      }, {}),
    );
  };

  const getICLAndLineByLineData = async () => {
    pageLoading();

    try {
      const [icl, lineByLinePayload, associatedOrders] = await Promise.all([
        invoiceService.getById(params.id),
        invoiceService.aggregatedAnalyseAndExport([params.id]),
        invoiceService.getOrdersByInvoiceControlListId(params.id),
      ]);

      setInvoiceControlList(icl);
      setAssociatedOrders(associatedOrders);
      setHasAssociatedOrders(!!associatedOrders.length);
      formatLineByLineData(lineByLinePayload);
    } catch {
      showErrorMessage(
        i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.FETCH_LINE_BY_LINE_DATA_ERROR'),
      );
    } finally {
      pageLoaded();
    }
  };

  const handlePriceIconMouseEnter = (row) => {
    if (!row.orderId && !row.spId) {
      return;
    }

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };

    if (row.updatePriceChecked) {
      newFormattedData[row.rowKey].updatePriceIcon = '/images/inpulse/close-white-small.svg';
      setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
      return;
    }

    newFormattedData[row.rowKey].updatePriceIcon = '/images/inpulse/new-price-black-small.svg';
    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
  };

  const handlePriceIconMouseLeave = (row) => {
    if (!row.orderId && !row.spId) {
      return;
    }

    if (row.updatePriceChecked) {
      const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };
      newFormattedData[row.rowKey].updatePriceIcon = '/images/inpulse/new-price-black-small.svg';
      setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
    }
  };

  const handlePackagingIconMouseEnter = (row) => {
    if (!row.orderId && !row.spId) {
      return;
    }

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };

    newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/stock-black-small.svg';

    if (row.updatePackagingChecked) {
      newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/close-white-small.svg';
    }

    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
  };

  const handlePackagingIconMouseLeave = (row) => {
    if (!row.orderId && !row.spId) {
      return;
    }

    if (row.updatePackagingChecked) {
      const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };
      newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/stock-black-small.svg';
      setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
    }
  };

  const handlePackagingDropdownTrigger = (row) => {
    const isDisabled =
      row.spCurrentMasterUnit !== row.spMasterUnit ||
      (row.spPackagings && row.spPackagings.length === 1) ||
      row.status === INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL ||
      hasSecondaryPricesForSupplierProduct(row);

    if (isDisabled || (!row.orderId && !row.spId)) {
      return;
    }

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };

    newFormattedData[row.rowKey].displayPackagingDropdown = !row.displayPackagingDropdown;

    if (!row.displayPackagingDropdown && row.updatePackagingChecked) {
      newFormattedData[row.rowKey].displayPackagingDropdown = false;
      newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/stock-black-small.svg';
      newFormattedData[row.rowKey].updatePackagingChecked = false;
      newFormattedData[row.rowKey].updatedInvoicePackaging = row.spInvoicePackaging;
      newFormattedData[row.rowKey].updatedSpPackagingName = row.spPackagingName;
      newFormattedData[row.rowKey].updatedSpPackagings = row.spPackagings;
      newFormattedData[row.rowKey].updatedSpReceivedQuantity = row.spReceivedQuantity;

      const updatedPrice = row.updatePriceChecked ? row.updatedSpPrice : row.spPrice;

      newFormattedData[row.rowKey].updatedSpPrice = updatedPrice;
      newFormattedData[row.rowKey].updatedReceivedTotal = updatedPrice * row.spReceivedQuantity;
      newFormattedData[row.rowKey].updatedTotalGap = round(
        (updatedPrice * row.spReceivedQuantity || 0) - (row.invoicedTotal || 0),
        3,
      );
    }

    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
  };

  const handleSelectRowToUpdatePrice = (row) => {
    const isDisabled =
      !row.updatePriceChecked &&
      (row.invoiceSpPrice == null ||
        row.updatedSpReceivedQuantity == null ||
        round(row.invoiceSpPrice, 3) === round(row.updatedSpPrice, 3) ||
        hasSecondaryPricesForSupplierProduct(row));

    if (isDisabled || (!row.orderId && !row.spId)) {
      return;
    }

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };
    newFormattedData[row.rowKey].updatePriceChecked = !row.updatePriceChecked;

    const updatedPrice = !row.updatePriceChecked
      ? row.updatePackagingChecked
        ? convertPriceToPackagingById(
            row.spPrice,
            row.spInvoicePackaging.id,
            row.updatedInvoicePackaging.id,
            row.updatedSpPackagings,
          )
        : row.spPrice
      : row.invoiceSpPrice;
    newFormattedData[row.rowKey].updatedSpPrice = updatedPrice;

    const receivedTotal =
      updatedPrice != null && row.updatedSpReceivedQuantity != null
        ? updatedPrice * row.updatedSpReceivedQuantity
        : null;
    newFormattedData[row.rowKey].updatedReceivedTotal = receivedTotal;

    const totalGap =
      receivedTotal != null || row.invoicedTotal != null
        ? round((receivedTotal || 0) - (row.invoicedTotal || 0), 3)
        : null;
    newFormattedData[row.rowKey].updatedTotalGap = totalGap;

    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
    handlePriceIconMouseEnter(newFormattedData[row.rowKey]);
  };

  const handleSelectRowToUpdatePackaging = (row, newPackaging) => {
    const isDisabled =
      row.spCurrentMasterUnit !== row.spMasterUnit ||
      (row.spPackagings && row.spPackagings.length === 1) ||
      row.status === INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL;

    if (
      isDisabled ||
      newPackaging.id === row.updatedInvoicePackaging.id ||
      (!row.orderId && !row.spId)
    ) {
      return;
    }

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };
    newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/stock-black-small.svg';
    newFormattedData[row.rowKey].updatePackagingChecked =
      newPackaging.id !== row.spInvoicePackaging.id;

    const updatedInvoicePackaging = newPackaging;
    newFormattedData[row.rowKey].updatedInvoicePackaging = updatedInvoicePackaging;

    const updatedPackagingName = get(updatedInvoicePackaging, 'name', row.spPackagingName);
    newFormattedData[row.rowKey].updatedSpPackagingName = updatedPackagingName;

    const updatedSpPackagings = row.spPackagings.map((packaging) => ({
      ...packaging,
      isUsedInInvoice: packaging.id === newPackaging.id,
    }));
    newFormattedData[row.rowKey].updatedSpPackagings = updatedSpPackagings;

    const updatedQuantity = convertQuantityToPackagingById(
      row.updatedSpReceivedQuantity,
      row.updatedInvoicePackaging.id,
      newPackaging.id,
      updatedSpPackagings,
    );
    newFormattedData[row.rowKey].updatedSpReceivedQuantity = updatedQuantity;

    const updatedPrice = row.updatePriceChecked
      ? row.updatedSpPrice
      : convertPriceToPackagingById(
          row.spPrice,
          row.spInvoicePackaging.id,
          newPackaging.id,
          updatedSpPackagings,
        );
    newFormattedData[row.rowKey].updatedSpPrice = updatedPrice;

    const receivedTotal =
      updatedPrice != null && updatedQuantity != null ? updatedPrice * updatedQuantity : null;
    newFormattedData[row.rowKey].updatedReceivedTotal = receivedTotal;

    const totalGap =
      receivedTotal != null || row.invoicedTotal != null
        ? round((receivedTotal || 0) - (row.invoicedTotal || 0), 3)
        : null;
    newFormattedData[row.rowKey].updatedTotalGap = totalGap;

    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
  };

  const resetAllRows = () => {
    const rowToReset = Object.values(formattedLineByLineDataByOrderIdAndSpId).reduce((acc, row) => {
      if (row.updatePriceChecked || row.updatePackagingChecked) {
        acc.push(row);
      }

      return acc;
    }, []);

    const newFormattedData = { ...formattedLineByLineDataByOrderIdAndSpId };

    rowToReset.forEach((row) => {
      if (!row.orderId && !row.spId) {
        return;
      }

      const spPrice = getSpPrice(row);

      newFormattedData[row.rowKey].updatePriceIcon = '/images/inpulse/new-price-black-small.svg';
      newFormattedData[row.rowKey].updatePriceChecked = false;
      newFormattedData[row.rowKey].updatedSpReceivedQuantity =
        row.spReceivedQuantity != null ? row.spReceivedQuantity : null;
      newFormattedData[row.rowKey].updatedSpPrice = spPrice != null ? spPrice : null;
      newFormattedData[row.rowKey].updatedReceivedTotal = row.receivedTotal;
      newFormattedData[row.rowKey].updatedTotalGap = row.totalGap;
      newFormattedData[row.rowKey].updatedInvoicePackaging =
        row.spInvoicePackaging != null ? row.spInvoicePackaging : null;
      newFormattedData[row.rowKey].updatedSpPackagingName = row.spPackagingName;
      newFormattedData[row.rowKey].updatedSpPackagings = row.spPackagings;
      newFormattedData[row.rowKey].updatePackagingIcon = '/images/inpulse/stock-black-small.svg';
      newFormattedData[row.rowKey].updatePackagingChecked = false;
      newFormattedData[row.rowKey].displayPackagingDropdown = false;
    });

    setFormattedLineByLineDataByOrderIdAndSpId(newFormattedData);
  };

  const handleUpdateOrderPrices = () => {
    const nbLineToBeUpdated = Object.values(formattedLineByLineDataByOrderIdAndSpId).filter(
      ({ updatePriceChecked }) => updatePriceChecked,
    ).length;

    const params = {
      type: 'warning',
      width: '542px',
      height: 'auto',
      icon: '/images/inpulse/warning-white-small.svg',
      title: i18next.t(
        'INVOICE.INVOICE_CONTROL_LINE_BY_LINE.UPDATE_ORDER_PRICES_CONFIRMATION_MODAL_TITLE',
      ),
      component: ConfirmationModal,
      data: {
        content: i18next.t(
          'INVOICE.INVOICE_CONTROL_LINE_BY_LINE.UPDATE_ORDER_PRICES_CONFIRMATION_MODAL_CONTENT',
          {
            count: nbLineToBeUpdated,
          },
        ),
      },
      actions: [
        GENERIC_MODAL_CANCEL_BUTTON(),
        {
          ...GENERIC_MODAL_CONFIRM_BUTTON(),
          handleClick: updateOrderData,
        },
      ],
    };

    openGenericModal(params);
  };

  const updateOrderData = async () => {
    const rowToUpdate = Object.values(formattedLineByLineDataByOrderIdAndSpId).filter(
      ({ updatePriceChecked, updatePackagingChecked }) =>
        updatePriceChecked || updatePackagingChecked,
    );

    const updatedInvoiceControlDetails = rowToUpdate.map((row) => ({
      orderId: row.orderId,
      spId: row.spId,
      price: row.updatedSpPrice != null ? row.updatedSpPrice : null,
      quantity: row.updatedSpReceivedQuantity != null ? row.updatedSpReceivedQuantity : null,
      packagings: row.updatedSpPackagings || row.spPackagings,
    }));

    pageLoading();
    try {
      await invoiceService.updateOrderData(updatedInvoiceControlDetails);

      const fetchedLineByLineData = await invoiceService.aggregatedAnalyseAndExport([
        invoiceControlList.id,
      ]);

      formatLineByLineData(fetchedLineByLineData);

      showSuccessMessage(
        i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.UPDATE_ORDER_DATA_SUCCESS'),
      );
    } catch {
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.UPDATE_ORDER_PRICES_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const refreshLineByLineData = async (manualMappedIds = manuallyMappedInvoiceControlIds) => {
    const pendingRowsByKey = Object.values(formattedLineByLineDataByOrderIdAndSpId).reduce(
      (acc, row) => {
        if (row.updatePriceChecked || row.updatePackagingChecked) {
          acc[getFormattedLineByLineDataKey(row)] = row;
        }

        return acc;
      },
      {},
    );

    const fetchedLineByLineData = await invoiceService.aggregatedAnalyseAndExport([
      invoiceControlList.id,
    ]);

    const mergedLineByLineData = fetchedLineByLineData.map((row, index) => ({
      ...row,
      ...pendingRowsByKey[getFormattedLineByLineDataKey(row, index)],
    }));

    formatLineByLineData(mergedLineByLineData, manualMappedIds);
  };

  const createInvoiceControlMapping = async (row) => {
    const orderId = getSelectedOrderId(row);
    const supplierProductId = getSelectedSupplierProductId(row);

    if (!orderId || !supplierProductId) {
      return;
    }

    pageLoading();

    try {
      await invoiceService.createInvoiceControlMapping(
        invoiceControlList.id,
        row.invoiceControlId,
        orderId,
        supplierProductId,
      );

      const manualMappedIds = [...manuallyMappedInvoiceControlIds, row.invoiceControlId];

      await refreshLineByLineData(manualMappedIds);

      const updatedDraftMappingsByInvoiceControlId = { ...draftMappingsByInvoiceControlId };
      delete updatedDraftMappingsByInvoiceControlId[row.invoiceControlId];
      setManuallyMappedInvoiceControlIds(manualMappedIds);
      setDraftMappingsByInvoiceControlId(updatedDraftMappingsByInvoiceControlId);
    } catch {
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.CREATE_MAPPING_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const deleteInvoiceControlMapping = async (row) => {
    if (!row.invoiceControlMappingId) {
      return;
    }

    pageLoading();

    try {
      await invoiceService.deleteInvoiceControlMapping(
        invoiceControlList.id,
        row.invoiceControlMappingId,
      );

      const manualMappedIds = manuallyMappedInvoiceControlIds.filter(
        (invoiceControlId) => invoiceControlId !== row.invoiceControlId,
      );
      setManuallyMappedInvoiceControlIds(manualMappedIds);

      await refreshLineByLineData(manualMappedIds);
    } catch {
      showErrorMessage(i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.DELETE_MAPPING_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const getSpPrice = ({ spPrice, spSecondPrice, spThirdPrice }) => {
    if (!useSupplierProductSecondaryPrices) {
      return spPrice;
    }

    return spSecondPrice ?? spThirdPrice ?? spPrice;
  };

  const getDisabledActionsText = (row) => {
    if (hasSecondaryPricesForSupplierProduct(row)) {
      return i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.DISABLE_ACTION_USING_SECONDARY_PRICE');
    }

    if (row.spCurrentMasterUnit !== row.spMasterUnit) {
      return i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.DISABLE_ACTION_TOOLTIP_MASTER_UNIT');
    }

    return '';
  };

  const hasSecondaryPricesForSupplierProduct = ({ spSecondPrice, spThirdPrice }) =>
    useSupplierProductSecondaryPrices && (spSecondPrice != null || spThirdPrice != null);

  return (
    <>
      <NavBarContainer>
        <NavigationBar
          bigTopBar={true}
          hasAssociatedOrders={hasAssociatedOrders}
          invoiceControlList={invoiceControlList || {}}
          path={path}
        />
      </NavBarContainer>
      <Container isFooterDisplay={isFooterDisplay}>
        <HeaderContainer>
          <Franco
            background={invoiceTotalGap < 0 ? theme?.colors?.infoRed : theme?.colors?.infoGreen}
            color={`${theme.colors.greys.lightest}`}
            content={<></>}
            labelContent={
              <DisplayNumber
                color={ENUM_COLORS.LIGHTEST}
                currency={currency}
                font={ENUM_FONTS.TEXT_COMPONENT_LABEL}
                number={invoiceTotalGap}
              />
            }
            title={i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.TOTAL_GAP')}
            hasBorder
          />
          <Franco
            background={
              conformLines !== filteredFormattedLineByLineData.length
                ? theme?.colors?.infoRed
                : theme?.colors?.infoGreen
            }
            color={`${theme.colors.greys.lightest}`}
            content={
              <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                {conformLines}/{filteredFormattedLineByLineData.length}
              </Text>
            }
            labelContent={
              <Text color={ENUM_COLORS.LIGHTEST} font={ENUM_FONTS.TEXT_COMPONENT_LABEL}>
                {round((conformLines / filteredFormattedLineByLineData.length) * 100)}%
              </Text>
            }
            title={i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.NB_CONFORM_LINES')}
            hasBorder
          />
          <ToggleContainer>
            <ToggleSwitch
              checked={displayProductOrderNotMapped}
              handleClick={() => {
                setDisplayProductOrderNotMapped(!displayProductOrderNotMapped);
              }}
              id={`display-product-order-not-mapped-toggle`}
              key={'display-product-order-not-mapped-toggle'}
            />
            <Text>
              {i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.DISPLAY_ALL_ORDERS_LINES')}
            </Text>
          </ToggleContainer>
        </HeaderContainer>
        <TableContainer
          component={Paper}
          sx={{
            overflow: 'visible',
            borderRadius: '8px',
            '& td:first-of-type, & th:first-of-type': {
              borderLeft: '1px solid #D7D7D7',
            },
            '& td:last-of-type, & th:last-of-type': {
              borderRight: '1px solid #D7D7D7',
            },
            '& thead tr:first-child th': {
              top: '88px',
              borderTop: '1px solid #D7D7D7',
            },
            '& thead tr:first-child th:first-child': {
              borderTopLeftRadius: '8px',
            },
            '& thead tr:first-child th:last-child': {
              borderTopRightRadius: '8px',
            },
            '& thead tr:last-child th': { top: '137px' },
            '& tbody tr:last-child td': {
              borderBottom: '1px solid #D7D7D7',
            },
            '& tbody tr:last-child td:first-child': {
              borderBottomLeftRadius: '8px',
            },
            '& tbody tr:last-child td:last-child': {
              borderBottomRightRadius: '8px',
            },
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: `${theme.colors.greys.lightest}`,
                zIndex: 2,
              }}
            >
              <TableRow>
                <TableCell align="center" colSpan={4}>
                  {i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.INVOICE')}
                </TableCell>
                <TableCell align="center" colSpan={1} sx={{ padding: 0 }}>
                  <EmptyDivContainer>
                    <EmptyDiv style={{ borderRight: `1px solid ${theme.colors.greys.medium}` }} />
                    <EmptyDiv />
                  </EmptyDivContainer>
                </TableCell>
                <TableCell align="center" colSpan={5}>
                  {i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.ORDERS')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{i18next.t('GENERAL.NAME')}</TableCell>
                <TableCell>{i18next.t('GENERAL.QUANTITY')}</TableCell>
                <TableCell>{i18next.t('GENERAL.PRICE_EX_TAX')}</TableCell>
                <TableCell>{i18next.t('GENERAL.TOTAL_EX_TAX')}</TableCell>
                <TableCell
                  align="center"
                  sx={{ backgroundColor: `${theme.colors.greys.lightyMedium}` }}
                >
                  {i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.GAP')}
                </TableCell>
                <TableCell>{i18next.t('GENERAL.NAME')}</TableCell>
                <TableCell>{i18next.t('GENERAL.QUANTITY')}</TableCell>
                <TableCell>{i18next.t('GENERAL.PRICE_EX_TAX')}</TableCell>
                <TableCell>{i18next.t('GENERAL.TOTAL_EX_TAX')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFormattedLineByLineData.map((row, index, array) => {
                const disabledText = getDisabledActionsText(row);
                const selectedOrderId = getSelectedOrderId(row);
                const referenceOptions = getAvailableReferenceOptions(
                  selectedOrderId,
                  row.invoiceControlId,
                );
                const selectedSupplierProductId = getSelectedSupplierProductId(row);
                const orderDropdownItems = formatOrderDropdownItems();
                const referenceDropdownItems = formatReferenceDropdownItems(referenceOptions);
                const isMappingCreationDisabled = !selectedOrderId || !selectedSupplierProductId;

                return (
                  <React.Fragment key={index}>
                    {isInvoiceControlMappedToOrder(row) &&
                      (index === 0 || row.orderId !== array[index - 1].orderId) && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              backgroundColor: `${theme.colors.greys.lightyMedium}`,
                            }}
                          ></TableCell>
                          <TableCell
                            colSpan={1}
                            sx={{
                              backgroundColor: `${theme.colors.greys.lightyMedium}`,
                            }}
                          ></TableCell>
                          <TableCell
                            colSpan={5}
                            sx={{
                              backgroundColor: `${theme.colors.greys.lightyMedium}`,
                            }}
                          >
                            {i18next.t(
                              'INVOICE.INVOICE_CONTROL_LINE_BY_LINE.ORDER_REFERENCE_LINE',
                              {
                                orderReference: row.orderReference,
                                deliveryDate: moment(row.orderDeliveryDate).format(
                                  DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR,
                                ),
                              },
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    {isInvoiceControlNotMappedToOrder(row) &&
                      (index === 0 || !isInvoiceControlNotMappedToOrder(array[index - 1])) && (
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{
                              backgroundColor: `${theme.colors.greys.lightyMedium}`,
                            }}
                          >
                            <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                              {i18next.t('INVOICE.INVOICE_CONTROL_LINE_BY_LINE.UNMAPPED_LINES')}
                            </Text>
                          </TableCell>
                          <TableCell
                            colSpan={5}
                            sx={{
                              backgroundColor: `${theme.colors.greys.lightyMedium}`,
                            }}
                          >
                            <MappingContainer>
                              <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                                {i18next.t('GENERAL.ORDER_NOUN')}
                              </Text>
                              <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                                {i18next.t('GENERAL.SUPPLIER_PRODUCT')}
                              </Text>
                              <div />
                            </MappingContainer>
                          </TableCell>
                        </TableRow>
                      )}
                    <TableRow>
                      {/* Invoice Part */}
                      {row.status === INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL && (
                        <TableCell align="center" colSpan={4}>
                          <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                            {i18next.t(
                              'INVOICE.INVOICE_CONTROL_LINE_BY_LINE.NO_CORRESPONDING_INVOICE_CONTROL_LINE',
                            )}
                          </Text>
                        </TableCell>
                      )}
                      {row.status !== INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL && (
                        <>
                          <TableCell>
                            <InvoiceLineNameContainer>
                              {row.isManuallyMapped && <AddedLineIndicator />}
                              <InvoiceLineTextContainer>
                                <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                                  {row.invoiceSpName || '-'}
                                </Text>
                                <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.CHART_LABEL}>
                                  {row.invoiceSku || '-'}
                                </Text>
                              </InvoiceLineTextContainer>
                            </InvoiceLineNameContainer>
                          </TableCell>
                          <TableCell>
                            <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>
                              {row.invoiceSpQuantity != null
                                ? round(row.invoiceSpQuantity, 3)
                                : '-'}
                            </Text>
                            <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.CHART_LABEL}>
                              {row.invoicePackaging || '-'}
                            </Text>
                          </TableCell>
                          <TableCell>
                            {row.invoiceSpPrice != null ? round(row.invoiceSpPrice, 3) : '-'}
                          </TableCell>
                          <TableCell>
                            {row.invoicedTotal != null ? round(row.invoicedTotal, 3) : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell
                        align="center"
                        sx={{
                          color:
                            row.status === INVOICE_CONTROL_STATUS.NO_GAP ||
                            row.updatedTotalGap === 0
                              ? `${theme.colors.infoGreen}`
                              : `${theme.colors.infoRed}`,
                          font: `${theme.fonts.textSmallHeight14Weight700}`,
                          backgroundColor: `${theme.colors.greys.lightyMedium}`,
                        }}
                      >
                        {row.updatedTotalGap != null ? round(row.updatedTotalGap, 3) : '-'}
                      </TableCell>
                      {/* Command Part */}
                      {isInvoiceControlNotMappedToOrder(row) && (
                        <TableCell colSpan={5}>
                          <MappingContainer>
                            <Dropdown
                              isDisabled={associatedOrders.length <= 1}
                              items={orderDropdownItems}
                              selectedItem={
                                orderDropdownItems.find(({ id }) => id === selectedOrderId) || null
                              }
                              width={'100%'}
                              onSelectionChange={(selectedOrder) => {
                                if (!selectedOrder) {
                                  return;
                                }

                                handleMappingOrderChange(row, selectedOrder.id);
                              }}
                            />
                            <Dropdown
                              isDisabled={!selectedOrderId || !referenceOptions.length}
                              items={referenceDropdownItems}
                              placeholder={i18next.t(
                                'INVOICE.INVOICE_CONTROL_LINE_BY_LINE.SELECT_SUPPLIER_INGREDIENT',
                              )}
                              selectedItem={
                                referenceDropdownItems.find(
                                  ({ id }) => id === selectedSupplierProductId,
                                ) || null
                              }
                              width={'100%'}
                              onSelectionChange={(selectedReference) => {
                                if (!selectedReference) {
                                  return;
                                }

                                handleMappingReferenceChange(row, selectedReference.id);
                              }}
                            />
                            <IconButtonContainer
                              isDisabled={isMappingCreationDisabled}
                              isBorderless
                              onClick={() => {
                                if (isMappingCreationDisabled) {
                                  return;
                                }

                                createInvoiceControlMapping(row);
                              }}
                            >
                              <IconButton src={'/images/inpulse/save-black-small.svg'} />
                            </IconButtonContainer>
                          </MappingContainer>
                        </TableCell>
                      )}
                      {isInvoiceControlMappedToOrder(row) && (
                        <>
                          <TableCell>
                            <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}>{row.spName || '-'}</Text>
                            <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.CHART_LABEL}>
                              {row.spSku || '-'}
                            </Text>
                          </TableCell>
                          <TableCell>
                            <Text
                              color={
                                row.updatedSpReceivedQuantity != null &&
                                row.invoiceSpQuantity != null &&
                                round(row.updatedSpReceivedQuantity, 3) !==
                                  round(row.invoiceSpQuantity, 3)
                                  ? ENUM_COLORS.INFO_RED
                                  : ENUM_COLORS.DARKEST
                              }
                              font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
                            >
                              {row.updatedSpReceivedQuantity != null
                                ? round(row.updatedSpReceivedQuantity, 3)
                                : '-'}
                            </Text>
                            <Text color={ENUM_COLORS.DGREY} font={ENUM_FONTS.CHART_LABEL}>
                              {row.updatedSpPackagingName || '-'}
                            </Text>
                          </TableCell>
                          <TableCell
                            sx={{
                              color:
                                row.updatedSpPrice != null &&
                                row.invoiceSpPrice != null &&
                                round(row.updatedSpPrice, 3) !== round(row.invoiceSpPrice, 3)
                                  ? `${theme.colors.infoRed}`
                                  : `${theme.colors.greys.darkest}`,
                            }}
                          >
                            {row.updatedSpPrice != null ? round(row.updatedSpPrice, 3) : '-'}
                          </TableCell>
                          <TableCell>
                            {row.updatedReceivedTotal != null
                              ? round(row.updatedReceivedTotal, 3)
                              : '-'}
                          </TableCell>
                          {/* Action Part */}
                          <TableCell sx={{ paddingY: 0, paddingRight: '16px' }}>
                            <ActionsContainer
                              style={{
                                width: '100%',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                              }}
                            >
                              <HoverTooltip
                                isTooltipDisplayed={!!disabledText}
                                renderContent={() => <DisabledTooltip text={disabledText} />}
                              >
                                <IconButtonContainer
                                  checked={row.updatePackagingChecked}
                                  isDisabled={
                                    !row.updatePackagingChecked &&
                                    (row.spCurrentMasterUnit !== row.spMasterUnit ||
                                      (row.spPackagings && row.spPackagings.length === 1) ||
                                      row.status ===
                                        INVOICE_CONTROL_STATUS.NO_CORRESPONDING_INVOICE_CONTROL ||
                                      hasSecondaryPricesForSupplierProduct(row))
                                  }
                                  onClick={() => handlePackagingDropdownTrigger({ ...row })}
                                  onMouseEnter={() =>
                                    handlePackagingIconMouseEnter({ ...row }, index)
                                  }
                                  onMouseLeave={() =>
                                    handlePackagingIconMouseLeave({ ...row }, index)
                                  }
                                >
                                  <IconButton src={row.updatePackagingIcon} />
                                  {row.displayPackagingDropdown && (
                                    <PackagingListContainer
                                      isLastRow={
                                        index === filteredFormattedLineByLineData.length - 1
                                      }
                                      ref={packagingListContainerRef}
                                    >
                                      {row.updatedSpPackagings.map((packaging) => (
                                        <PackagingContainer
                                          key={packaging.id}
                                          onClick={() =>
                                            handleSelectRowToUpdatePackaging(
                                              { ...row },
                                              { ...packaging },
                                            )
                                          }
                                        >
                                          <Text
                                            font={ENUM_FONTS.TEXT_SMALL_HEIGHT_16}
                                            style={{
                                              textOverflow: 'ellipsis',
                                              overflow: 'hidden',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {packaging.name}
                                          </Text>
                                          {packaging.id ===
                                            get(row, 'updatedInvoicePackaging.id', null) && (
                                            <IconButton
                                              src={'/images/inpulse/done-black-small.svg'}
                                            />
                                          )}
                                        </PackagingContainer>
                                      ))}
                                    </PackagingListContainer>
                                  )}
                                </IconButtonContainer>
                              </HoverTooltip>
                              <HoverTooltip
                                isTooltipDisplayed={!!disabledText}
                                renderContent={() => <DisabledTooltip text={disabledText} />}
                              >
                                <IconButtonContainer
                                  checked={row.updatePriceChecked}
                                  isDisabled={
                                    !row.updatePriceChecked &&
                                    (row.invoiceSpPrice == null ||
                                      row.updatedSpReceivedQuantity == null ||
                                      round(row.invoiceSpPrice, 3) ===
                                        round(row.updatedSpPrice, 3) ||
                                      row.spCurrentMasterUnit !== row.spMasterUnit ||
                                      hasSecondaryPricesForSupplierProduct(row))
                                  }
                                  onClick={() => handleSelectRowToUpdatePrice(row)}
                                  onMouseEnter={() => handlePriceIconMouseEnter(row, index)}
                                  onMouseLeave={() => handlePriceIconMouseLeave(row, index)}
                                >
                                  <IconButton src={row.updatePriceIcon} />
                                </IconButtonContainer>
                              </HoverTooltip>
                              <IconButtonContainer
                                style={{
                                  visibility: row.isManuallyMapped ? 'visible' : 'hidden',
                                  pointerEvents: row.isManuallyMapped ? 'auto' : 'none',
                                }}
                                isBorderless
                                onClick={() => {
                                  deleteInvoiceControlMapping(row);
                                }}
                              >
                                <IconButton src={'/images/inpulse/delete-black-small.svg'} />
                              </IconButtonContainer>
                            </ActionsContainer>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
      {isFooterDisplay && (
        <Footer>
          <FooterButtonsContainer>
            <Button
              color={'inpulse-outline'}
              handleClick={resetAllRows}
              icon={'/images/inpulse/close-black-small.svg'}
              label={i18next.t('GENERAL.CANCEL')}
            />
            <Button
              color={'inpulse-default'}
              handleClick={handleUpdateOrderPrices}
              icon={'/images/inpulse/save-white-small.svg'}
              label={i18next.t('GENERAL.SAVE')}
            />
          </FooterButtonsContainer>
        </Footer>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  useSupplierProductSecondaryPrices: getUseSupplierProductSecondaryPrices(
    state.baseReducer.userRights,
  ),
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceControlLineByLine);
